<template>
  <div class="flex flex-col gap-5">
    <!--
      The two-way choice, as a radio group rather than two buttons — the same
      pattern the ownership step itself uses for the same reason: one tab
      stop, arrow keys between the cards, and picking one is an answer, not
      an action.
    -->
    <DSRadioGroup
      :model-value="mode"
      class="grid-cols-1 gap-2.5 sm:grid-cols-2"
      aria-label="How this mortgage is chosen"
      @update:model-value="handleModeChange"
    >
      <DSChoiceCard
        :value="WizardAccountMode.EXISTING"
        :icon="FolderOpen"
        title="Select an existing mortgage"
        description="Search loans already in this repository."
      />
      <DSChoiceCard
        :value="WizardAccountMode.NEW"
        :icon="FolderPlus"
        title="Create a new mortgage"
        description="Name the lender — it is filed as a new liability."
      />
    </DSRadioGroup>

    <!-- 1a — pick a liability the repository already has. -->
    <div
      v-if="!isCreating"
      ref="accountCell"
      class="min-w-0"
    >
      <DSFieldLabel
        class="mb-1.5"
        :for="accountInputId"
        :label="idField.label"
        :mark="markFor(origins[idField.name])"
      />

      <AccountPicker
        :id="accountInputId"
        :model-value="accountId"
        :accounts
        :suggestion
        :loading="hasSentence && suggestionStore.isLoading"
        :askable
        :placeholder="idField.placeholder"
        empty-label="Nothing to pick — create a new mortgage instead."
        @select="handlePick"
        @ask="handleAskSuggestion"
      >
        <template #note>
          Loan accounts only. Feed-maintained accounts are excluded — those are owned by the connection.
        </template>
      </AccountPicker>
    </div>

    <!-- 1b — name the lender; the debt itself is created fresh. -->
    <template v-else>
      <WizardField
        :form
        :field="nameField"
        :origin="origins[nameField.name]"
      />

      <!--
        Said before it happens, not after: nothing is written until the
        records are, so backing out of the wizard cannot leave a stray
        mortgage behind — the same promise the ownership step's own create-new
        half makes.
      -->
      <DSAlert tone="info">
        <template #icon>
          <Info />
        </template>
        <DSAlertDescription>
          The new mortgage is created alongside the records at save, not before —
          cancelling leaves nothing behind.
        </DSAlertDescription>
      </DSAlert>
    </template>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-x-5">
      <!--
      The amount owed either way — a fresh mortgage's own opening balance, or
      what is being added against an existing one. Never conditional on the
      mode above: create() always posts this as the liability transaction's
      own amount.
    -->
      <WizardField
        :form
        :field="principalField"
        :origin="origins[principalField.name]"
      />

      <!--
      The mortgage's own description — never the property's. Also
      unconditional on the mode: it lands on the liability transaction either
      way, see create.ts.
    -->
      <WizardField
        :form
        :field="descriptionField"
        :origin="origins[descriptionField.name]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { FolderOpen, FolderPlus, Info } from '@lucide/vue'

import { DSAlert, DSAlertDescription, DSChoiceCard, DSFieldLabel, DSRadioGroup } from '@ds'

import AccountPicker from '@/entities/assets/components/AccountPicker.vue'
import { AccountOption } from '@/entities/assets/utils/types'
import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { AssetWizardAccount, AssetWizardInvestor } from '@/store/ai/assetWizard/utils/types'
import { useAIMortgageAccountSuggestionStore } from '@/store/ai/mortgageAccountSuggestion'
import { useAssetsBunchStore } from '@/store/assets/bunch'

import WizardField from '../../components/WizardField.vue'

import { MORTGAGE_ACCOUNT_MODE_FIELD } from '../../utils/const'
import { useWizardFieldFocus } from '../../utils/focus'
import { markFor } from '../../utils/helpers'
import { WizardAccountMode, WizardFieldDef, WizardFieldOrigin } from '../../utils/types'
import { MORTGAGE_TYPE } from '../create'

/*
 * The one step the generic field grid cannot express: an existing/new choice
 * for the mortgage, the same either/or the ownership step itself asks — see
 * OwnershipStep.vue, which this mirrors down to the create-new half's own
 * "not written until save" alert. The mortgage's own "type" no longer exists
 * (every mortgage this wizard writes is filed the same fixed way, see
 * create.ts), so this step is that one minus its type half — just an account
 * to pick or a lender to name — and minus its second assistant: there is no
 * type left to ask "what kind of thing is this" about.
 */
const { form, fields, origins, flow, sentence } = defineProps<{
  form: any
  fields: WizardFieldDef[]
  origins: Record<string, WizardFieldOrigin>
  /**
   * Declared, not used: Wizard.vue hands every bespoke step the transaction it
   * serves — the property's own `add position` — and an undeclared prop would
   * land on this component's root as a stray attribute. The mortgage's own
   * candidates are scoped by asset type directly (see `accounts` below),
   * never by a transaction's own settings.
   */
  transactionType?: string
  /** The wizard being filled in, as context for the suggested mortgage. */
  flow: string
  /**
   * Also declared, also unused: the account the read resolved is the one that
   * *owns* the property, and this step picks the liability it is financed by.
   * Two different accounts, and only the ownership step fills from that one.
   */
  ownerAccount?: AssetWizardAccount
  /** Declared for the same reason: only private_fund ever carries this, never this flow. */
  investors?: AssetWizardInvestor[]
  /** What the user typed, if they typed anything — the sentence the assistant reads. */
  sentence?: string
}>()

const assetsBunchStore = useAssetsBunchStore()

const suggestionStore = useAIMortgageAccountSuggestionStore()

const fieldByName = (name: string) => fields.find(field => field.name === name)!

const idField = computed(() => fieldByName('accountId'))
const nameField = computed(() => fieldByName('accountName'))
const principalField = computed(() => fieldByName('principal'))
const descriptionField = computed(() => fieldByName('accountDescription'))

const accountInputId = useId()

/*
 * The account is the one required field in this step that is not a
 * WizardField, so it registers itself: a banner chip naming it would
 * otherwise open this step and leave the user to find the picker.
 */
const accountCell = ref<HTMLElement>()

useWizardFieldFocus(() => idField.value.name, accountCell)

/**
 * Existing loan-type assets this mortgage could already be — read directly
 * off the bunch store's own type field, not through `useWizardOptions().
 * accountsOf()`. That helper narrows to `accountTypesFor(transactionType)`,
 * the repository's own configured vocabulary for what a *new* liability may
 * be filed as — a real, separate setting from what already exists. create()
 * writes a mortgage as type 'loan' regardless of that setting (the write
 * skips the same check, see `postTransaction`'s `check: false`), so a
 * repository whose own liability settings do not happen to list "loan"
 * would otherwise never find the very mortgages this wizard already made.
 */
const accounts = computed<AccountOption[]>(() => {
  const options: AccountOption[] = []
  for (const asset of assetsBunchStore.getUnlinkedList.values()) {
    if (asset.field<string>(ASSET_FIELD.TYPE).value !== MORTGAGE_TYPE) continue
    options.push({
      id: asset.id,
      name: asset.field<string>(ASSET_FIELD.NAME).value,
      ticker: asset.field<string>(ASSET_FIELD.TICKER).value,
      type: MORTGAGE_TYPE,
    })
  }
  return options
})

const hasSentence = computed(() => Boolean(sentence?.trim()))

const suggestion = computed(() => (hasSentence.value ? suggestionStore.getSuggestion : undefined))

/*
 * Offered only with a sentence to read: "which of these is the mortgage"
 * cannot be answered from no words at all, so a click here would spend a
 * model call to be told nothing fits. Hidden once asked, whatever the
 * answer — the same gate the ownership step's own account suggestion uses.
 */
const askable = computed(() => hasSentence.value && !suggestionStore.hasAsked)

/*
 * Read off the form rather than held here: the step is remounted every time
 * the user walks back into it, and a mode kept in component state would
 * forget which half they were filling in.
 */
const mode = form.useSelector((state: any) => state.values[MORTGAGE_ACCOUNT_MODE_FIELD])
const accountId = form.useSelector((state: any) => state.values.accountId)

const isCreating = computed(() => mode.value === WizardAccountMode.NEW)

/*
 * Switching modes clears both fields. The alternative — keeping the name of
 * an account the user has just stopped pointing at — leaves a value nobody
 * chose in a field the schema is about to require differently.
 */
const handleModeChange = (next: unknown) => {
  form.setFieldValue(MORTGAGE_ACCOUNT_MODE_FIELD, next)
  form.setFieldValue('accountId', '')
  form.setFieldValue('accountName', '')
}

/** Picking copies the account's own name in beside its id. */
const handlePick = (account: AccountOption) => {
  form.setFieldValue('accountId', account.id)
  form.setFieldValue('accountName', account.name)
}

const handleAskSuggestion = () => {
  suggestionStore.suggest({
    sentence: sentence!,
    flow,
    transactionType: TRANSACTION_TYPE.ADD_LIABILITY,
    accounts: accounts.value,
  })
}
</script>
